import { getDefaultStore, useSetAtom, type createStore } from 'jotai';
import { useCallback } from 'react';

import { injected } from '../injected-auth';
import { clearComposerLinkedItemsAtom, closeArtifactAtom } from './artifacts/state';
import {
  clearComposerDraftStorage,
  composerDraftStorageKey,
} from './hooks/useComposerDraftPersistence';
import { pendingComposerLinkedContextAtom } from './pendingComposerLinkedContext';
import { clearPendingWorkTaskLaunches } from './work-home-tasks';

type JotaiStore = ReturnType<typeof createStore>;
interface ResetProjectsComposerContextOptions {
  /** Entry reset preserves the one-shot hand-off for the following drain. */
  preservePendingLinkedContext?: boolean;
}

/**
 * Drop the Work-home / project composer linked chip and canvas artifact.
 *
 * The library document preview belongs to the app's document library, so the
 * caller passes in the clear for it; this module never reads that state.
 */
export function resetProjectsComposerContextCore(
  clearLibraryDocumentPreview: (store: JotaiStore) => void,
  store: JotaiStore = getDefaultStore(),
  persistedDraftProjectId?: string,
  options: ResetProjectsComposerContextOptions = {}
): void {
  store.set(clearComposerLinkedItemsAtom);
  store.set(closeArtifactAtom);
  if (!options.preservePendingLinkedContext) {
    store.set(pendingComposerLinkedContextAtom, null);
  }
  clearLibraryDocumentPreview(store);
  clearPendingWorkTaskLaunches();
  if (persistedDraftProjectId) {
    clearComposerDraftStorage(composerDraftStorageKey(persistedDraftProjectId));
  }
}

/**
 * The same reset as a hook, over the component's store. The library clear must
 * keep a stable identity: the returned callback's identity follows it.
 */
export function useResetProjectsComposerContextCore(
  clearLibraryDocumentPreview: () => void
): (
  persistedDraftProjectId?: string,
  options?: ResetProjectsComposerContextOptions
) => void {
  const clearLinkedItems = useSetAtom(clearComposerLinkedItemsAtom);
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const clearPendingLinkedContext = useSetAtom(pendingComposerLinkedContextAtom);

  return useCallback(
    (
      persistedDraftProjectId?: string,
      options: ResetProjectsComposerContextOptions = {}
    ) => {
      clearLinkedItems();
      closeArtifact();
      if (!options.preservePendingLinkedContext) clearPendingLinkedContext(null);
      clearLibraryDocumentPreview();
      clearPendingWorkTaskLaunches();
      if (persistedDraftProjectId) {
        clearComposerDraftStorage(composerDraftStorageKey(persistedDraftProjectId));
      }
    },
    [
      clearLibraryDocumentPreview,
      clearLinkedItems,
      clearPendingLinkedContext,
      closeArtifact,
    ]
  );
}

const clearLibraryPreview = (store?: JotaiStore) => {
  injected('clearLibraryDocumentPreview')(store);
};

export function resetProjectsComposerContext(
  store?: JotaiStore,
  persistedDraftProjectId?: string,
  options?: ResetProjectsComposerContextOptions
): void {
  resetProjectsComposerContextCore(
    clearLibraryPreview,
    store,
    persistedDraftProjectId,
    options
  );
}

export function useResetProjectsComposerContext(): ReturnType<
  typeof useResetProjectsComposerContextCore
> {
  return useResetProjectsComposerContextCore(clearLibraryPreview);
}
